import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import type { ServiceCode } from '@/common/order.common'

export type ServiceValues = Partial<Record<ServiceCode, number>>

const SERVICES: { key: ServiceCode; label: string }[] = [
  { key: 'TOJ', label: 'TOJ' },
  { key: 'CONSULTING', label: 'Beratung' },
  { key: 'TRAINING', label: 'Training' },
  { key: 'PILOT', label: 'Pilot' },
]

interface Props {
  values: ServiceValues
  /** When set, each cell renders "value aus capacity". */
  capacities?: ServiceValues
  /** Green treatment for remaining-capacity cells; negatives turn red. */
  variant?: 'plain' | 'capacity'
  /** Format the primary value (e.g. currency). Defaults to a plain number. */
  formatValue?: (value: number) => string
  /** Font size for the primary value. Defaults to "2xl". */
  valueFontSize?: string
  /** Hide the Pilot cell when its value (and capacity) are zero/undefined. */
  hidePilotWhenZero?: boolean
}

export function ServiceStatBoxes({
  values,
  capacities,
  variant = 'plain',
  formatValue,
  valueFontSize = '2xl',
  hidePilotWhenZero = false,
}: Props) {
  const cells = SERVICES.filter(s => {
    if (!hidePilotWhenZero || s.key !== 'PILOT') return true
    return (values.PILOT ?? 0) !== 0 || (capacities?.PILOT ?? 0) !== 0
  })

  return (
    <HStack gap={2} align="stretch" wrap="wrap">
      {cells.map(s => {
        const value = values[s.key] ?? 0
        const capacity = capacities?.[s.key]
        const isCapacity = variant === 'capacity'
        const negative = isCapacity && value < 0
        const accent = negative ? 'red.fg' : isCapacity ? 'green.fg' : undefined

        return (
          <Box
            key={s.key}
            flex="1"
            minW="62px"
            px={isCapacity ? 2.5 : 0}
            py={isCapacity ? 2 : 0}
            borderWidth={isCapacity ? '1px' : '0'}
            borderRadius="lg"
            borderColor={negative ? 'red.muted' : 'green.muted'}
            bg={negative ? 'red.subtle' : isCapacity ? 'green.subtle' : 'transparent'}
          >
            <VStack align="start" gap={0}>
              <Text fontSize="sm" color={accent ?? 'fg.muted'}>
                {s.label}
              </Text>
              <Text fontSize={valueFontSize} fontWeight="semibold" color={accent ?? 'fg.default'}>
                {formatValue ? formatValue(value) : value}
              </Text>
              {capacity != null && (
                <Text fontSize="xs" color={accent ?? 'fg.muted'}>
                  aus {formatValue ? formatValue(capacity) : capacity}
                </Text>
              )}
            </VStack>
          </Box>
        )
      })}
    </HStack>
  )
}
